import { jsx as _jsx } from "react/jsx-runtime";
import { onValue, ref } from "firebase/database";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { realtimeDb } from "../firebase/config";
import { useTeams } from "../teams/TeamProvider";
import { getAdminSupportTeamId } from "../admin/adminSupport";
const AccessContext = createContext(undefined);
export function AccessProvider({ children }) {
    const { user, isAdmin } = useAuth();
    const { selectedTeam } = useTeams();
    const [access, setAccess] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    useEffect(() => {
        if (!selectedTeam) {
            setAccess(null);
            setLoading(false);
            setError("");
            return;
        }
        setAccess(null);
        setLoading(true);
        setError("");
        return onValue(ref(realtimeDb, `access/${selectedTeam.id}`), (snapshot) => {
            setAccess(snapshot.val() ?? {});
            setLoading(false);
        }, (caughtError) => {
            setAccess(null);
            setError(caughtError.message);
            setLoading(false);
        });
    }, [selectedTeam]);
    const value = useMemo(() => {
        const userId = user?.uid;
        const adminSupportTeamId = isAdmin ? getAdminSupportTeamId() : "";
        const isAdminSupportOwner = Boolean(selectedTeam && adminSupportTeamId === selectedTeam.id);
        const isTeamOwner = Boolean(isAdminSupportOwner || (userId && (access?.owner === userId || selectedTeam?.owner.id === userId)));
        const hasTeamWriteAccess = Boolean(userId && (isTeamOwner || access?.managers?.[userId] === true));
        const seasonAccess = (seasonId) => seasonId ? access?.seasons?.[seasonId] ?? null : null;
        const hasSeasonWriteAccess = (seasonId) => Boolean(userId && seasonId && (hasTeamWriteAccess || access?.seasons?.[seasonId]?.managers?.[userId] === true));
        const canWriteSeason = (seasonId) => hasSeasonWriteAccess(seasonId) && access?.seasons?.[seasonId]?.locked !== true;
        return { access, loading, error, isTeamOwner, hasTeamWriteAccess, seasonAccess, hasSeasonWriteAccess, canWriteSeason };
    }, [access, loading, error, isAdmin, selectedTeam, user]);
    return _jsx(AccessContext.Provider, { value: value, children: children });
}
export function useAccess() {
    const context = useContext(AccessContext);
    if (!context)
        throw new Error("useAccess must be used within AccessProvider");
    return context;
}
